import type { ArtifactItem } from '@eriko/shared-types';
import {
  isInlineArtifactType,
  isMatchConfirmedArtifactData,
  isPaymentConfirmedArtifactData,
  isReconciliationListArtifactData,
} from '@eriko/shared-types';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  ChatAvatar,
  ChatBubble
} from '@eriko/ui/src/components/ui/chat';
import { cjk } from '@streamdown/cjk';
import { code } from '@streamdown/code';
import { math } from '@streamdown/math';
import { mermaid } from '@streamdown/mermaid';
import { FileBarChart } from 'lucide-react';
import type { ComponentProps } from 'react';
import { Streamdown } from 'streamdown';
import { MatchConfirmedCard } from '../../../components/chat-artifacts/MatchConfirmedCard';
import { PaymentConfirmedCard } from '@eriko/ui/src/chat-artifacts/PaymentConfirmedCard';
import { ReconciliationListCard } from '../../../components/chat-artifacts/ReconciliationListCard';
import type { ChatMessage } from '../../../stores/chatStore';
import { DotGridLoader } from './DotGridLoader';
import { InteractivePromptCard } from './InteractivePromptCard';
import { useRotatingVerb } from './useRotatingVerb';

type StreamdownComponents = ComponentProps<typeof Streamdown>['components'];

interface AssistantMessageProps {
  msg: ChatMessage;
  status: string | null;
  artifactIndices: number[];
  artifacts: ArtifactItem[];
  onViewArtifact: (index: number) => void;
  mdComponents?: StreamdownComponents;
  isLatestAssistant: boolean;
  onAutoSubmit: () => void;
}

function preprocessEntityLinks(content: string): string {
  return content.replace(/\]\(entity:([^)]+)\)/g, '](#entity-$1)');
}

const proseClasses =
  'text-[15px] leading-relaxed text-surface-900 prose prose-sm max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4 [&>table]:w-full [&>table]:border-collapse [&_th]:bg-surface-100 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_th]:border [&_th]:border-surface-200 [&_td]:px-3 [&_td]:py-1.5 [&_td]:border [&_td]:border-surface-200 [&_td]:text-sm';

export function AssistantMessage({
  msg,
  status,
  artifactIndices,
  artifacts,
  onViewArtifact,
  mdComponents,
  isLatestAssistant,
  onAutoSubmit,
}: AssistantMessageProps) {
  const rotatingVerb = useRotatingVerb(!!msg.streaming);

  const myArtifacts = artifactIndices
    .map((i) => artifacts[i])
    .filter((a): a is ArtifactItem => !!a);
  const inlineArtifacts = myArtifacts.filter((a) => isInlineArtifactType(a.type));
  const panelArtifactIndex = artifactIndices.find((i) => {
    const a = artifacts[i];
    return a && !isInlineArtifactType(a.type);
  });
  return (
    <div className="flex gap-3 animate-[fade-up_0.25s_ease-out]">
      <ChatAvatar variant="bot" className="mt-0" />
      <div className="max-w-[78%] mt-2">
        <ChatBubble variant="assistant">
          <div className={proseClasses}>
            <Streamdown
              mode={msg.streaming ? 'streaming' : 'static'}
              plugins={{ code, math, mermaid, cjk }}
              components={mdComponents}
            >
              {preprocessEntityLinks(msg.content)}
            </Streamdown>
            {msg.streaming && !msg.content && (
              status ? (
                <div className="flex items-center gap-2">
                  <DotGridLoader />
                  <span className="text-surface-500 italic text-sm">{status}</span>
                </div>
              ) : rotatingVerb ? (
                <div className="flex items-center gap-2">
                  <DotGridLoader />
                  <span
                    key={rotatingVerb}
                    className="text-surface-500 italic text-sm animate-[fade-up_0.25s_ease-out]"
                  >
                    {rotatingVerb}…
                  </span>
                </div>
              ) : null
            )}
          </div>
          {msg.metadata?.interactivePrompt && (
            <InteractivePromptCard
              messageId={msg.dbMessageId ?? msg.id}
              prompt={msg.metadata.interactivePrompt}
              isLatest={isLatestAssistant}
              onAutoSubmit={onAutoSubmit}
            />
          )}
          {!msg.streaming &&
            inlineArtifacts.map((a, idx) => {
              if (a.type === 'reconciliation-list' && isReconciliationListArtifactData(a.data)) {
                return <ReconciliationListCard key={`recon-${idx}`} data={a.data} />;
              }
              if (a.type === 'match-confirmed' && isMatchConfirmedArtifactData(a.data)) {
                return <MatchConfirmedCard key={`confirm-${idx}`} data={a.data} />;
              }
              if (a.type === 'payment-confirmed' && isPaymentConfirmedArtifactData(a.data)) {
                return <PaymentConfirmedCard key={`payment-${idx}`} data={a.data} />;
              }
              return null;
            })}
        </ChatBubble>
        {!msg.streaming && panelArtifactIndex !== undefined && (
          <div className="flex items-center gap-1.5 mt-2 pl-1">
            <Button
              variant="outline"
              size="sm"
              className="h-auto px-2.5 py-1 text-[11px]"
              onClick={() => onViewArtifact(panelArtifactIndex)}
            >
              <FileBarChart className="w-3 h-3" />
              View Summary
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
